import { createApi } from "@reduxjs/toolkit/query/react";

import { axiosBaseQuery } from "@/api/base";
import { FileItem, FolderItem, type FsEntryAccess } from "@/types/fse";
import axios from "axios";

export const FILES_TAG = "files";
export const FILES_TAG_SEARCH = "search";

export type SearchFilesParamsEntryType = "file" | "folder";

interface SearchFilesParams {
  depth?: number;
  entry_type: SearchFilesParamsEntryType[];
  query: string;
  fse_id?: string;
  only_shared?: boolean;
}

export const filesApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: "/pf-document/" }),
  reducerPath: "filesApi",
  tagTypes: [FILES_TAG, FILES_TAG_SEARCH],
  endpoints: builder => ({
    createFolder: builder.mutation<FolderItem, { name: string; parent_id: string }>({
      invalidatesTags: [FILES_TAG],
      query: ({ name, parent_id }) => ({
        url: "mkdir",
        method: "POST",
        data: { name, fse_id: parent_id },
      }),
    }),
    createPermission: builder.mutation<void, { fse_id: string; role: string; users: string[] }>({
      invalidatesTags: [FILES_TAG],
      query: ({ fse_id, role, users }) => ({
        url: `permissions/${fse_id}`,
        method: "POST",
        data: { permissions: users.map(user => ({ role, user_id: user, fse_id })) },
      }),
    }),
    updateFileAccess: builder.mutation<void, { fse_id: string; access: FsEntryAccess }>({
      invalidatesTags: [FILES_TAG],
      query: ({ fse_id, access }) => ({
        url: `permissions/${fse_id}`,
        method: "PATCH",
        data: { access },
      }),
    }),
    removeFile: builder.mutation<void, string>({
      invalidatesTags: [FILES_TAG],
      query: fse_id => ({ url: "rm", method: "POST", data: { fse_id } }),
    }),
    renameFile: builder.mutation<void, { fse_id: string; name: string }>({
      invalidatesTags: [FILES_TAG],
      query: ({ fse_id, name }) => ({
        url: "rename-file",
        method: "POST",
        data: { fse_id, name },
      }),
    }),
    deletePermission: builder.mutation<void, { fse_id: string; permission_id: string }>({
      invalidatesTags: [FILES_TAG],
      query: ({ fse_id, permission_id }) => ({
        url: `permissions/${fse_id}/${permission_id}`,
        method: "DELETE",
      }),
    }),
    linkFile: builder.mutation<void, { fse_id: string; linked_fse_ids: string[] }>({
      invalidatesTags: [FILES_TAG],
      query: ({ fse_id, linked_fse_ids }) => ({
        url: "link-files",
        method: "POST",
        data: { fse_id, linked_fse_ids },
      }),
    }),
    search: builder.query<FileItem[], SearchFilesParams>({
      providesTags: [FILES_TAG_SEARCH],
      query: ({ depth = 1, entry_type, query, fse_id, only_shared }) => ({
        url: "search",
        params: {
          depth,
          only_shared,
          fse_id: fse_id || undefined,
          query,
          entry_type,
        },
      }),
    }),
    getFileDetails: builder.query<
      FileItem | FolderItem,
      Partial<{ fse_id?: string; only_shared?: boolean; depth?: number }>
    >({
      providesTags: [FILES_TAG],
      query: ({ fse_id, only_shared, depth }) => ({
        url: "ls",
        params: { only_shared, fse_id: fse_id || undefined, depth },
      }),
    }),
  }),
});

interface CheckFileExistsResponse {
  exists: boolean;
}

export const checkFileExists = async (name: string): Promise<boolean> => {
  const response = await axios.get<CheckFileExistsResponse>("/pf-document/check-document-exists", {
    params: { name },
  });

  return response.data.exists;
};

export const {
  useLazyGetFileDetailsQuery,
  useGetFileDetailsQuery,
  useSearchQuery,
  useCreateFolderMutation,
  useRenameFileMutation,
  useRemoveFileMutation,
  useLinkFileMutation,
  useUpdateFileAccessMutation,
  useCreatePermissionMutation,
  useDeletePermissionMutation,
} = filesApi;
